import { ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { getTenantStorageKey, useRole } from "../contexts/RoleContext";
import NotificationBell, { NotificationItem } from "./NotificationBell";
import AppTutorial, { EMPLOYEE_TOUR_STEPS } from "./AppTutorial";
import { useTheme } from "../contexts/ThemeContext";
import { useEntitlement, useEntitlementRevalidation } from "../contexts/EntitlementContext";
import { useManager } from "../contexts/ManagerContext";
import { formatTemplate, useLanguage } from "../contexts/LanguageContext";
import { SECURITY_MODULES, TOPIC_LABELS_BY_ID, localizeModule, normalizeTrainingTopicId } from "../data/securityModules";
import { authFetch, clearPersistedApiCache } from "../utils/authFetch";
import { useModuleTitle } from "../hooks/useModuleTitle";
import { confirmLeaveIfGuarded } from "../utils/leaveGuard";
import { clearMemberSession, getMemberSession, identityEmail, identityName } from "../utils/memberSession";

type MenuLabelKey = "navDashboard" | "navTraining" | "navTest" | "navTeam" | "navSettings";

interface LayoutProps {
  children: ReactNode;
  hideAdminLink?: boolean;
}


type TrainingRecord = {
  topicId: string;
  score: number;
  completedAt: string;
  validUntil?: string;
};

type TestResult = {
  score: number;
  total: number;
  passed: boolean;
  completedAt: string;
};

function normalizeUserEmail(email: string): string {
  return email.trim().toLowerCase();
}

// Die Tour öffnet sich nur beim allerersten Login von selbst. Wer schon vor
// Einführung der Tour angemeldet war, bekommt sie nicht nachträglich
// aufgedrängt – findet sie aber über das „?".
const TUTORIAL_AUTOSTART_SINCE = Date.parse("2026-09-25T00:00:00Z");

function tutorialSeenKey(tenantId: string, email: string): string {
  return `sherloxaware_tutorial_seen_${tenantId}_${email}`;
}




const employeeNavItems = [
  {
    path: "/dashboard",
    labelKey: "navDashboard" as MenuLabelKey,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
        <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
      </svg>
    ),
  },
  {
    path: "/lernmodule",
    labelKey: "navTraining" as MenuLabelKey,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path d="M11.7 2.805a.75.75 0 01.6 0A60.65 60.65 0 0122.83 8.72a.75.75 0 01-.231 1.337 49.949 49.949 0 00-9.902 3.912l-.003.002-.34.18a.75.75 0 01-.707 0A50.009 50.009 0 007.5 12.174v-.224c0-.131.067-.248.172-.311a54.614 54.614 0 014.653-2.52.75.75 0 00-.65-1.352 56.129 56.129 0 00-4.78 2.589 1.858 1.858 0 00-.859 1.228 49.803 49.803 0 00-4.634-1.527.75.75 0 01-.231-1.337A60.653 60.653 0 0111.7 2.805z" />
        <path d="M13.06 15.473a48.45 48.45 0 017.666-3.282c.134 1.414.22 2.843.255 4.285a.75.75 0 01-.46.71 47.878 47.878 0 00-8.105 4.342.75.75 0 01-.832 0 47.877 47.877 0 00-8.104-4.342.75.75 0 01-.461-.71c.035-1.442.121-2.87.255-4.286A48.4 48.4 0 016 13.18v1.27a1.5 1.5 0 00-.14 2.508c-.09.38-.222.753-.397 1.11.452.213.901.434 1.346.661a6.729 6.729 0 00.551-1.608 1.5 1.5 0 00.14-2.67v-.645a48.549 48.549 0 013.44 1.668 2.25 2.25 0 002.12 0z" />
        <path d="M4.462 19.462c.42-.419.753-.89 1-1.394.453.213.902.434 1.347.661a6.743 6.743 0 01-1.286 1.794.75.75 0 11-1.06-1.06z" />
      </svg>
    ),
  },
  {
    path: "/test",
    labelKey: "navTest" as MenuLabelKey,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path fillRule="evenodd" d="M7.502 6h7.128A3.375 3.375 0 0118 9.375v9.375a3 3 0 003-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 00-.673-.05A3 3 0 0015 1.5h-1.5a3 3 0 00-2.663 1.618c-.225.015-.45.032-.673.05C8.662 3.295 7.554 4.542 7.502 6zM13.5 3A1.5 1.5 0 0012 4.5h4.5A1.5 1.5 0 0015 3h-1.5z" clipRule="evenodd" />
        <path fillRule="evenodd" d="M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 013 20.625V9.375zM6 12a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H6.75a.75.75 0 01-.75-.75V12zm2.25 0a.75.75 0 01.75-.75h3.75a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75zM6 15a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H6.75a.75.75 0 01-.75-.75V15zm2.25 0a.75.75 0 01.75-.75h3.75a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75zM6 18a.75.75 0 01.75-.75h.008a.75.75 0 01.75.75v.008a.75.75 0 01-.75.75H6.75a.75.75 0 01-.75-.75V18zm2.25 0a.75.75 0 01.75-.75h3.75a.75.75 0 010 1.5H9a.75.75 0 01-.75-.75z" clipRule="evenodd" />
      </svg>
    ),
  },
  {
    path: "/einstellungen",
    labelKey: "navSettings" as MenuLabelKey,
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
        <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clipRule="evenodd" />
      </svg>
    ),
  },
];

export default function Layout({ children, hideAdminLink = false }: LayoutProps) {
  const [, setNotificationRefreshVersion] = useState(0);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarOpenMobile, setSidebarOpenMobile] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialSidebar, setTutorialSidebar] = useState(false);
  const sidebarOpen = sidebarOpenMobile || sidebarHovered || tutorialSidebar;
  const setSidebarOpen = setSidebarOpenMobile;
  const [isMobile, setIsMobile] = useState(false);
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAdmin, isContentAdmin, tenantId } = useRole();
  // Content-Admins sehen vom Admin-Bereich nur die Modulbearbeitung.
  const showAdminLink = (isAdmin || isContentAdmin) && !hideAdminLink;
  const adminHome = isAdmin ? "/admin" : "/admin/eigene-module";
  const { isDark, toggleTheme } = useTheme();
  const { entitlement } = useEntitlement();
  // Themen und Farben sollen nach einer Änderung im Admin-Bereich ohne Neuladen ankommen.
  useEntitlementRevalidation(pathname);
  const { isManager } = useManager();
  const { t, language } = useLanguage();
  const moduleTitle = useModuleTitle(tenantId, language === "en" ? "en" : "de");
  const account = instance.getActiveAccount() ?? accounts[0];
  const memberSession = getMemberSession();
  const userEmail = identityEmail(account?.username);
  const personName = identityName(account?.name);
  const normalizedEmail = normalizeUserEmail(userEmail);

  // Training completions and test result from API (no localStorage)
  const [apiTrainingByTopic, setApiTrainingByTopic] = useState<Record<string, TrainingRecord>>({});
  const [apiTestResult, setApiTestResult] = useState<TestResult | null>(null);

  useEffect(() => {
    if (!tenantId || !normalizedEmail) return;
    const fetchNotificationData = () => {
      authFetch(`/api/completions?tenantId=${encodeURIComponent(tenantId)}&email=${encodeURIComponent(normalizedEmail)}`)
        .then(r => r.ok ? (r.json() as Promise<{ topicId: string; score: number; completedAt: string; validUntil?: string }[]>) : [])
        .then(data => {
          const byTopic: Record<string, TrainingRecord> = {};
          data.forEach(r => {
            const topicId = normalizeTrainingTopicId(r.topicId) ?? r.topicId;
            const prev = byTopic[topicId];
            if (!prev || r.score > prev.score) byTopic[topicId] = { topicId, score: r.score, completedAt: r.completedAt, validUntil: r.validUntil };
          });
          setApiTrainingByTopic(byTopic);
        })
        .catch(() => {});
      authFetch(`/api/test-results?tenantId=${encodeURIComponent(tenantId)}&email=${encodeURIComponent(normalizedEmail)}`)
        .then(r => r.ok ? (r.json() as Promise<{ score: number; total: number; passed: boolean; completedAt: string }[]>) : [])
        .then(data => { if (data[0]) setApiTestResult(data[0]); })
        .catch(() => {});
    };
    fetchNotificationData();
    const intervalId = setInterval(fetchNotificationData, 90 * 1000);
    const onFocus = () => fetchNotificationData();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [tenantId, normalizedEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const testEnabled = entitlement?.features.test_enabled !== false;
  const visibleNavItems = employeeNavItems.filter(item => {
    if (item.path === "/test") return testEnabled;
    return true;
  });

  const now = new Date();
  const in30Days = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

  const completionEntries = Object.values(apiTrainingByTopic);
  const expiredModules = completionEntries.filter(e => e.validUntil && new Date(e.validUntil) < now);
  const expiringSoonModules = completionEntries.filter(e => e.validUntil && new Date(e.validUntil) >= now && new Date(e.validUntil) < in30Days);
  const lowScoreEntries = completionEntries.filter((entry) => entry.score < 8);
  const lastTestResult = apiTestResult;

  const localizedTopicLabel = (topicId: string): string => {
    const module = SECURITY_MODULES.find(m => m.topicId === topicId);
    if (!module) return TOPIC_LABELS_BY_ID[topicId] ?? moduleTitle(null, topicId);
    const shortTitle = localizeModule(module, language).shortTitle;
    return language === "en" ? `${shortTitle} quiz` : `${shortTitle}-Quiz`;
  };

  const notifications: NotificationItem[] = [];

  if (expiredModules.length > 0) {
    notifications.push({
      id: `employee-expired-${expiredModules.map(e => e.topicId).sort().join("|")}`,
      title: t.notifications.expiredTitle,
      description: formatTemplate(t.notifications.expiredBody, { count: expiredModules.length }),
      level: "danger",
      href: "/lernmodule",
    });
  }

  if (expiringSoonModules.length > 0) {
    notifications.push({
      id: `employee-expiring-soon-${expiringSoonModules.map(e => e.topicId).sort().join("|")}`,
      title: t.notifications.expiringSoonTitle,
      description: formatTemplate(t.notifications.expiringSoonBody, { count: expiringSoonModules.length }),
      level: "warning",
      href: "/lernmodule",
    });
  }

  if (lowScoreEntries.length > 0) {
    const weakest = [...lowScoreEntries].sort((a, b) => a.score - b.score)[0];
    notifications.push({
      id: `employee-low-score-${weakest.topicId}-${weakest.completedAt}`,
      title: t.notifications.lowScoreTitle,
      description: formatTemplate(t.notifications.lowScoreBody, { topic: localizedTopicLabel(weakest.topicId), pct: weakest.score * 10 }),
      level: "warning",
      href: "/lernmodule",
    });
  }

  if (testEnabled && lastTestResult && !lastTestResult.passed) {
    notifications.push({
      id: `employee-test-failed-${lastTestResult.completedAt}`,
      title: t.notifications.testFailedTitle,
      description: formatTemplate(t.notifications.testFailedBody, { score: lastTestResult.score, total: lastTestResult.total }),
      level: "danger",
      href: "/lernmodule",
    });
  }

  // Persönliche Erinnerungen des Vorgesetzten (features.manager_notes[email]).
  const managerNotes = (entitlement?.features as { manager_notes?: Record<string, Array<{ id: string; fromName?: string; text?: string; at: string }>> } | undefined)?.manager_notes?.[normalizedEmail];
  if (Array.isArray(managerNotes)) {
    managerNotes.slice(-5).forEach(note => {
      notifications.push({
        id: `manager-note-${note.id}`,
        title: formatTemplate(t.notifications.managerReminderTitle, { name: note.fromName ?? t.common.employee }),
        description: (note.text && note.text.trim()) ? note.text.trim() : t.notifications.managerReminderBody,
        level: "warning",
        href: "/lernmodule",
      });
    });
  }

  const notificationPrefix = getTenantStorageKey("sherloxaware_notifications_read", tenantId);
  const notificationStorageKey = notificationPrefix
    ? `${notificationPrefix}_${normalizedEmail || "unknown"}`
    : `sherloxaware_notifications_read_unavailable_${normalizedEmail || "unknown"}`;

  // Sync Azure AD user data to Supabase every 3 minutes
  useEffect(() => {
    if (!tenantId || !userEmail || userEmail === "unknown") return;
    const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
    const userId = (typeof claims?.oid === "string" ? claims.oid : null)
      ?? account?.localAccountId
      ?? "";

    // Mitglieder eines Sammelaccounts werden serverseitig beim Sub-Login
    // gepflegt (users-Zeile + Profil). Hier würde der Client die Daten des
    // gemeinsamen Kontos schreiben – das Backend lehnt das ohnehin ab.
    if (memberSession) return;

    const syncUser = () => {
      const displayName = (account?.name ?? "").trim();
      // Write to users table (new)
      if (userId) {
        authFetch("/api/users", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: userId, tenantId, email: userEmail, displayName, isAdmin }),
        }).catch(() => {});
      }
      // Write display name to user-profiles (legacy, keep for admin name display)
      if (displayName) {
        authFetch("/api/user-profiles", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId, email: userEmail, displayName }),
        }).catch(() => {});
      }
    };
    syncUser();
    const id = setInterval(syncUser, 3 * 60 * 1000);
    return () => clearInterval(id);
  }, [tenantId, userEmail, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const refreshNotifications = () => setNotificationRefreshVersion((value) => value + 1);

    const patchFlag = "__sherloxawareStorageEventsPatched";
    const windowWithFlags = window as unknown as Record<string, unknown>;
    if (!windowWithFlags[patchFlag]) {
      const storagePrototype = Storage.prototype;
      const originalSetItem = storagePrototype.setItem;
      const originalRemoveItem = storagePrototype.removeItem;
      const originalClear = storagePrototype.clear;

      storagePrototype.setItem = function setItemWithEvent(key: string, value: string) {
        originalSetItem.call(this, key, value);
        if (key.startsWith("sherloxaware_")) {
          window.dispatchEvent(new Event("sherloxaware:storage-updated"));
        }
      };

      storagePrototype.removeItem = function removeItemWithEvent(key: string) {
        originalRemoveItem.call(this, key);
        if (key.startsWith("sherloxaware_")) {
          window.dispatchEvent(new Event("sherloxaware:storage-updated"));
        }
      };

      storagePrototype.clear = function clearWithEvent() {
        originalClear.call(this);
        window.dispatchEvent(new Event("sherloxaware:storage-updated"));
      };

      windowWithFlags[patchFlag] = true;
    }

    window.addEventListener("sherloxaware:storage-updated", refreshNotifications);
    window.addEventListener("storage", refreshNotifications);
    return () => {
      window.removeEventListener("sherloxaware:storage-updated", refreshNotifications);
      window.removeEventListener("storage", refreshNotifications);
    };
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpenMobile(false);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Einführungs-Tour beim allerersten Login. Nicht mitten in ein Modul hinein
  // (Deep-Link aus einer Mail) – dann eben beim nächsten Seitenwechsel.
  const inModule = pathname.startsWith("/training/");
  useEffect(() => {
    if (!tenantId || !normalizedEmail || normalizedEmail === "unknown" || inModule) return;
    const seenKey = tutorialSeenKey(tenantId, normalizedEmail);
    try { if (localStorage.getItem(seenKey)) return; } catch {}
    let cancelled = false;
    authFetch(`/api/user-profiles?tenantId=${encodeURIComponent(tenantId)}&email=${encodeURIComponent(normalizedEmail)}`)
      .then(r => r.ok ? r.json() : null)
      .then((profile: { tutorialSeenAt?: string; firstLogin?: string; registeredAt?: string } | null) => {
        if (cancelled || !profile) return;
        const firstLogin = Date.parse(profile.firstLogin ?? profile.registeredAt ?? "");
        const isNewUser = Number.isNaN(firstLogin) || firstLogin >= TUTORIAL_AUTOSTART_SINCE;
        if (profile.tutorialSeenAt || !isNewUser) {
          try { localStorage.setItem(seenKey, "1"); } catch {}
          return;
        }
        openTutorial();
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tenantId, normalizedEmail, inModule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Die Tour erklärt das Dashboard Kachel für Kachel – also dort starten.
  const openTutorial = () => {
    if (window.location.pathname !== "/dashboard" && confirmLeaveIfGuarded()) navigate("/dashboard");
    setTutorialOpen(true);
  };

  const closeTutorial = () => {
    setTutorialOpen(false);
    if (!tenantId || !normalizedEmail) return;
    try { localStorage.setItem(tutorialSeenKey(tenantId, normalizedEmail), "1"); } catch {}
    authFetch("/api/user-profiles", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId, email: normalizedEmail, tutorialSeen: true }),
    }).catch(() => {});
  };

  const tutorialSteps = useMemo(
    () => EMPLOYEE_TOUR_STEPS.filter(s => (s.id !== "test" || testEnabled) && (s.id !== "manager" || isManager)),
    [testEnabled, isManager],
  );

  const forceTutorialSidebar = useCallback((open: boolean) => setTutorialSidebar(open), []);

  const handleLogout = () => {
    if (!confirmLeaveIfGuarded()) return;
    clearPersistedApiCache();
    // Am Sammelaccount meldet sich nur die Person ab – das gemeinsame
    // Microsoft-Konto bleibt angemeldet, der Nächste gibt direkt seine
    // Mitarbeiternummer ein.
    if (memberSession) {
      clearMemberSession();
      window.location.reload();
      return;
    }
    instance.logoutRedirect();
  };

  // Kunden-Logo passend zum Theme; fehlt eine Variante, dient die andere als Fallback.
  const cobrandLogo = isDark
    ? (entitlement?.features.cobrand_logo_dark ?? entitlement?.features.cobrand_logo)
    : (entitlement?.features.cobrand_logo ?? entitlement?.features.cobrand_logo_dark);
  const cobrandOnDark = isDark && !!entitlement?.features.cobrand_logo_dark;

  return (
    <div style={{ display: "flex", height: "100vh", background: "var(--bg-page)", overflow: "hidden", position: "relative" }}>
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150 }} />
      )}
      {/* Sidebar */}
      <aside
        id="employee-sidebar"
        onMouseEnter={() => { if (!isMobile) setSidebarHovered(true); }}
        onMouseLeave={() => setSidebarHovered(false)}
        style={{
          width: isMobile ? "240px" : (sidebarOpen ? "240px" : "64px"),
          background: isDark ? "#161B22" : "#1B6AB1",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          overflow: "hidden",
          willChange: "width",
          transition: "width 160ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 160ms ease",
          boxShadow: !isMobile && sidebarHovered ? "4px 0 16px rgba(0,0,0,0.18)" : "none",
          ...(isMobile
            ? { position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 200, transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)" }
            : { position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 100 }),
        }}
      >
      <div style={{ width: isMobile ? "100%" : (sidebarOpen ? "240px" : "64px"), display: "flex", flexDirection: "column", flex: 1, minHeight: 0, height: "100%" }}>
        <div
          style={{
            boxSizing: "border-box",
            padding: "0 0.875rem",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              height: "56px",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              justifyContent: "flex-start",
            }}
          >
            <div
              style={{
                width: "36px",
                height: "36px",
                background: "#fff",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
              }}
            >
              <img
                src="/brand/aware-icon.png"
                alt="SHERLOX AWARE"
                width="30"
                height="30"
                style={{ display: "block", objectFit: "contain" }}
              />
            </div>
            {sidebarOpen && (
              <div style={{ minWidth: 0 }}>
                <div style={{ lineHeight: 1.1, whiteSpace: "nowrap" }}>
                  <span style={{ fontWeight: "700", fontSize: "1rem", color: "white" }}>SHERLOX</span>
                  <span style={{ fontWeight: "400", fontSize: "1rem", color: "white" }}> AWARE</span>
                </div>
                <div style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.65)", marginTop: "2px", letterSpacing: "0.03em", whiteSpace: "nowrap" }}>
                  Security Awareness
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav data-tour="nav" style={{ padding: "0.75rem 0.5rem", flex: 1, overflowY: "auto", minHeight: 0 }}>
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-tour={`nav-${item.path.slice(1)}`}
              onClick={(e) => { if (!confirmLeaveIfGuarded()) { e.preventDefault(); return; } if (isMobile) setSidebarOpen(false); }}
              style={({ isActive }) => ({
                display: "flex",
                alignItems: "center",
                gap: sidebarOpen ? "0.625rem" : "0",
                padding: "0.6rem 0.75rem",
                borderRadius: "6px",
                marginBottom: "2px",
                color: "rgba(255,255,255,0.9)",
                background: isActive ? "#1559A0" : "transparent",
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: isActive ? "600" : "400",
                transition: "background 0.15s ease",
                justifyContent: "flex-start",
              })}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                if (el.getAttribute("aria-current") !== "page") {
                  el.style.background = "rgba(255,255,255,0.12)";
                }
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLAnchorElement;
                if (el.getAttribute("aria-current") !== "page") {
                  el.style.background = "transparent";
                }
              }}
            >
              {item.icon}
              {sidebarOpen && t.menu[item.labelKey]}
            </NavLink>
          ))}

          {/* Bereichs-Wechsel unten: Manager- und/oder Admin-Bereich, damit man
              (als Admin) zwischen Mitarbeiter-, Manager- und Admin-Bereich wechseln kann. */}
          {(isManager || showAdminLink) && (
            <div style={{ marginTop: "1rem", paddingTop: "0.75rem", borderTop: "1px solid rgba(255,255,255,0.12)" }}>
              {isManager && (
                <NavLink
                  to="/manager"
                  data-tour="nav-manager"
                  onClick={(e) => { if (!confirmLeaveIfGuarded()) { e.preventDefault(); return; } if (isMobile) setSidebarOpen(false); }}
                  style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: sidebarOpen ? "0.625rem" : "0",
                    padding: "0.6rem 0.75rem",
                    borderRadius: "6px",
                    marginBottom: "2px",
                    color: isDark ? (isActive ? "#F97316" : "rgba(240,246,252,0.75)") : "var(--bg-card)",
                    background: isActive ? (isDark ? "rgba(249,115,22,0.15)" : "#1559A0") : "transparent",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    fontWeight: isActive ? "600" : "400",
                    justifyContent: "flex-start",
                  })}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                    <path d="M4.5 6.375a4.125 4.125 0 118.25 0 4.125 4.125 0 01-8.25 0zM14.25 8.625a3.375 3.375 0 116.75 0 3.375 3.375 0 01-6.75 0zM1.5 19.125a7.125 7.125 0 0114.25 0v.003l-.001.119a.75.75 0 01-.363.63 13.067 13.067 0 01-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 01-.364-.63l-.001-.122zM17.25 19.128l-.001.144a2.25 2.25 0 01-.233.96 10.088 10.088 0 005.06-1.01.75.75 0 00.42-.643 4.875 4.875 0 00-6.957-4.611 8.586 8.586 0 011.71 5.157v.003z" />
                  </svg>
                  {sidebarOpen && t.menu.navTeam}
                </NavLink>
              )}
              {showAdminLink && (
                <NavLink
                  to={adminHome}
                  onClick={(e) => { if (!confirmLeaveIfGuarded()) { e.preventDefault(); return; } if (isMobile) setSidebarOpen(false); }}
                  style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: sidebarOpen ? "0.625rem" : "0",
                    padding: "0.6rem 0.75rem",
                    borderRadius: "6px",
                    color: isDark ? (isActive ? "#F97316" : "rgba(240,246,252,0.75)") : "var(--bg-card)",
                    background: isActive ? (isDark ? "rgba(249,115,22,0.15)" : "#1559A0") : "transparent",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                    fontWeight: isActive ? "600" : "400",
                    justifyContent: "flex-start",
                  })}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                    <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clipRule="evenodd" />
                  </svg>
                  {sidebarOpen && (isAdmin ? t.menu.navAdmin : t.menu.navModuleEditing)}
                </NavLink>
              )}
            </div>
          )}
        </nav>

        {/* Cobrand-Logo unter der Nav statt im Header: die Nav startet dadurch
            in beiden Sidebar-Zuständen an derselben Y-Position und springt nicht. */}
        {sidebarOpen && cobrandLogo && (
          <div style={{ flexShrink: 0, padding: "0.625rem 0.875rem", borderTop: "1px solid rgba(255,255,255,0.12)" }}>
            <div style={{ background: cobrandOnDark ? "rgba(255,255,255,0.08)" : "#fff", borderRadius: "6px", padding: "5px 8px", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }}>
              <img src={cobrandLogo} alt={entitlement?.features.cobrand_name ?? "Logo"} style={{ maxHeight: "30px", maxWidth: "100%", objectFit: "contain", display: "block" }} />
            </div>
          </div>
        )}

        {/* User info at bottom */}
        <div
          style={{
            padding: "0.875rem 0.5rem",
            borderTop: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(0,0,0,0.15)",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", justifyContent: "flex-start" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.875rem",
                fontWeight: "700",
                color: "white",
                flexShrink: 0,
              }}
            >
              {personName.charAt(0).toUpperCase() || "U"}
            </div>
            {sidebarOpen && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                  <div style={{ fontSize: "0.8rem", color: "white", fontWeight: "500", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {personName || t.common.user}
                  </div>
                  {isAdmin && (
                    <span style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "4px", fontSize: "0.6rem", fontWeight: "700", padding: "1px 4px", flexShrink: 0 }}>
                      ADMIN
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {memberSession ? memberSession.username : (account?.username ?? "")}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </aside>

      {/* Main area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, height: "100vh", overflow: "hidden", marginLeft: isMobile ? 0 : "64px" }}>
        {/* Top navbar */}
        <header
          style={{
            height: "56px",
            background: "var(--bg-card)",
            borderBottom: "1px solid var(--border)",
            boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 1rem",
            gap: "0.75rem",
            flexShrink: 0,
          }}
        >
          {isMobile ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "36px", height: "36px", background: "transparent", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--text-secondary)", cursor: "pointer", flexShrink: 0 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path fillRule="evenodd" d="M3 6.75A.75.75 0 013.75 6h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 6.75zM3 12a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75A.75.75 0 013 12zm0 5.25a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H3.75a.75.75 0 01-.75-.75z" clipRule="evenodd" /></svg>
              </button>
              {showAdminLink && (
                <NavLink
                  to={adminHome}
                  onClick={(e) => { if (!confirmLeaveIfGuarded()) e.preventDefault(); }}
                  style={({ isActive }) => ({
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: "36px", height: "36px", borderRadius: "8px",
                    background: isActive ? "#1B6AB1" : "transparent",
                    border: "1px solid var(--border)",
                    color: isActive ? "#fff" : "var(--text-secondary)",
                    cursor: "pointer", flexShrink: 0,
                  })}
                  title={isAdmin ? t.menu.topAdminArea : t.menu.navModuleEditing}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                    <path fillRule="evenodd" d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z" clipRule="evenodd" />
                  </svg>
                </NavLink>
              )}
            </div>
          ) : <div />}
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {/* Einführungs-Tour */}
          <button
            data-tour="help"
            onClick={openTutorial}
            title={language === "en" ? "Tour & help" : "Einführung & Hilfe"}
            aria-label={language === "en" ? "Open tour" : "Einführung öffnen"}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: "transparent",
              border: "1px solid var(--border)",
              color: "var(--text-secondary)",
              cursor: "pointer",
              transition: "all 0.15s ease",
              flexShrink: 0,
              fontSize: "0.95rem",
              fontWeight: 700,
              fontFamily: "inherit",
            }}
            onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-card-alt)"; }}
            onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
          >
            ?
          </button>

          {/* Dark mode toggle */}
          <button
            data-tour="theme"
            onClick={toggleTheme}
            title={isDark ? t.menu.themeEnableLight : t.menu.themeEnableDark}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              background: "transparent",
              border: "1px solid var(--border)",
              color: "var(--text-secondary)",
              cursor: "pointer",
              transition: "all 0.15s ease",
              flexShrink: 0,
            }}
            onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "var(--bg-card-alt)"; }}
            onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
          >
            {isDark ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.166 17.834a.75.75 0 00-1.06 1.06l1.59 1.591a.75.75 0 001.061-1.06l-1.59-1.591zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.166 6.166a.75.75 0 001.06 1.06l1.591-1.59a.75.75 0 10-1.06-1.061l-1.591 1.59z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
              </svg>
            )}
          </button>

          <div data-tour="notifications" style={{ display: "flex" }}>
            <NotificationBell
              notifications={notifications}
              storageKey={notificationStorageKey}
              emptyText={t.menu.notificationEmpty}
              onNavigate={(href) => { if (!confirmLeaveIfGuarded()) return; navigate(href); }}
            />
          </div>

          {!isMobile && (
            <span style={{ fontSize: "0.875rem", color: "var(--text-secondary)", fontWeight: "500" }}>
              {personName}
            </span>
          )}

          {/* Logout button */}
          <button
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.375rem 0.875rem",
              background: "transparent",
              border: "1px solid #1B6AB1",
              borderRadius: "6px",
              color: "#1B6AB1",
              fontSize: "0.8rem",
              cursor: "pointer",
              fontWeight: "500",
              transition: "all 0.15s ease",
            }}
            onMouseOver={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "#1B6AB1";
              (e.currentTarget as HTMLButtonElement).style.color = "#ffffff";
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "transparent";
              (e.currentTarget as HTMLButtonElement).style.color = "#1B6AB1";
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9A.75.75 0 0115 9V5.25a1.5 1.5 0 00-1.5-1.5h-6zm10.72 4.72a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H9a.75.75 0 010-1.5h10.94l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
            </svg>
            {t.menu.topLogout}
          </button>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: isMobile ? "1rem" : "1.5rem", overflow: "auto", background: "var(--bg-page)" }}>
          <Suspense fallback={
            <div style={{ minHeight: "40vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)", fontSize: "0.85rem" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
                <div style={{ width: "32px", height: "32px", border: "3px solid var(--border)", borderTop: "3px solid #1B6AB1", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <span>{t.common.loading}</span>
              </div>
            </div>
          }>
            {children}
          </Suspense>
        </main>
      </div>

      <AppTutorial
        open={tutorialOpen}
        language={language}
        steps={tutorialSteps}
        finishLabel={{ de: "Zum Trainingsbereich", en: "Go to training" }}
        onClose={closeTutorial}
        onFinish={() => {
          closeTutorial();
          if (confirmLeaveIfGuarded()) navigate("/lernmodule");
        }}
        onSidebarForce={forceTutorialSidebar}
      />
    </div>
  );
}
